import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PLACEMENT } from 'baseui/tooltip'
import { useStyletron } from 'styletron-react'
import Tooltip from '@/components/Tooltip/Tooltip'
import BeforeAfterIcon from '../Icons/BeforeAfterIcon'
import { AI_GLOW_CIRCLE_STYLE } from '@/components/AiGlowCircle/AiGlowCircle'

type Placement = (typeof PLACEMENT)[keyof typeof PLACEMENT]

export interface BeforeAfterToggleButtonProps {
  onPressStart?: () => void | Promise<void>
  onPressEnd?: () => void | Promise<void>
  position?: { left: number; top: number }
  hideWhilePressed?: boolean
  visible?: boolean
  tooltipPlacement?: Placement
  zIndex?: number
}

export function BeforeAfterToggleButton({
  onPressStart,
  onPressEnd,
  position,
  hideWhilePressed = false,
  visible = true,
  tooltipPlacement = PLACEMENT.top,
  zIndex,
}: BeforeAfterToggleButtonProps) {
  const { t } = useTranslation()
  const [css] = useStyletron()
  const [isPressed, setIsPressed] = useState(false)
  const isPressedRef = useRef(false)
  const onPressEndRef = useRef(onPressEnd)

  useEffect(() => {
    onPressEndRef.current = onPressEnd
  }, [onPressEnd])

  const doRelease = useCallback(async () => {
    if (!isPressedRef.current) return
    isPressedRef.current = false
    setIsPressed(false)
    await onPressEndRef.current?.()
  }, [])

  // Global listeners catch the release even when the button is hidden (isPressed=true)
  useEffect(() => {
    if (!isPressed) return
    document.addEventListener('mouseup', doRelease)
    document.addEventListener('touchend', doRelease)
    document.addEventListener('touchcancel', doRelease)
    return () => {
      document.removeEventListener('mouseup', doRelease)
      document.removeEventListener('touchend', doRelease)
      document.removeEventListener('touchcancel', doRelease)
    }
  }, [isPressed, doRelease])

  // If the host unmounts mid-press, still fire onPressEnd so callers can clean up
  useEffect(() => {
    return () => {
      if (isPressedRef.current) {
        isPressedRef.current = false
        onPressEndRef.current?.()
      }
    }
  }, [])

  const handlePress = useCallback(async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    isPressedRef.current = true
    setIsPressed(true)
    await onPressStart?.()
  }, [onPressStart])

  if (!visible) return null
  if (hideWhilePressed && isPressed) return null

  return (
    <Tooltip info={{ content: t('See before / after'), placement: tooltipPlacement }}>
      <button
        className={css({
          ...AI_GLOW_CIRCLE_STYLE,
          ...(position
            ? { position: 'absolute', left: `${position.left}px`, top: `${position.top}px` }
            : { position: 'relative' }),
          border: 'none',
          padding: '0',
          appearance: 'none',
          cursor: 'pointer',
          ...(zIndex !== undefined ? { zIndex } : {}),
          userSelect: 'none',
          WebkitUserSelect: 'none',
          flexShrink: 0,
          ':hover': {
            boxShadow: AI_GLOW_CIRCLE_STYLE.boxShadow,
          },
          ':focus-visible': {
            outline: '2px solid rgba(124,58,237,0.8)',
            outlineOffset: '2px',
          },
        })}
        aria-label={t('See before / after')}
        onMouseDown={handlePress}
        onTouchStart={handlePress}
      >
        <BeforeAfterIcon size={24} />
      </button>
    </Tooltip>
  )
}

export default BeforeAfterToggleButton
